/**
 * PairPicker — the phone-first Y-site experience for the Matrix tab.
 *
 * The N×N grid (MatrixGrid) is a desktop/tablet artifact: at 390px it shows
 * ~3 rotated-header columns, forces horizontal panning to do anything, and its
 * verdict lives in a hover tooltip that never fires on touch. PairPicker is the
 * touch-native answer to the same question — "can A and B share a Y-site?":
 *
 *   1. Two slots, A and B. Each opens a full-screen searchable drug list.
 *   2. Once both are filled, the verdict renders immediately as a tone-coded
 *      card (colour + label + dot — never colour alone).
 *
 * It is a pure interaction surface over the board's existing axis slots
 * (selectedRowDrugId / selectedColDrugId). Picking A writes the row slot,
 * picking B the column slot — so DrugSidePanel + the verdict derivation the
 * board already owns light up unchanged. Clearing a slot clears that axis.
 *
 * Rendered while the board's own CONTAINER is below the 48rem knob in
 * compat.css (a phone, or a pane on any desktop); MatrixGrid takes over once
 * the container fits it. The swap never reads the viewport — a pane is not the
 * viewport. No data plumbing of its own — the board passes the same `state`,
 * `colDrugs`, selection, and callbacks the grid uses.
 */

import { useMemo, useState, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Pill, Plus, Search, X } from 'lucide-react'
import { useDeferredAutoFocus } from '@frontend/hooks/useDeferredAutoFocus'
import { useMedicationLookups } from '@frontend/hooks/useMedicationSearch'
import { useVisualViewportHeight } from '@frontend/hooks/useVisualViewportHeight'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import type {
  CompatibilityBoardState,
  CompatibilityCell,
  CompatibilityDrug,
  VerdictTone,
} from '@frontend/components/ysite/types'
import { getCell, VERDICT_TONE } from '@frontend/components/ysite/types'
import { drugSearchTerm } from './drugSearchTerm'
import { shortLabel, splitLabel } from '@frontend/components/ysite/label'
import { MedicationCard } from './MedicationCard'
import { TONE_FILL, TONE_TEXT, TONE_TINT } from '@frontend/components/ysite/verdict.ui'

interface PairPickerProps {
  state: CompatibilityBoardState
  /** Selectable drugs (non-sentinel) — the same list the grid columns use. */
  colDrugs: CompatibilityDrug[]
  /** Row-axis slot value (drug A). */
  drugA: CompatibilityDrug | null
  /** Column-axis slot value (drug B). */
  drugB: CompatibilityDrug | null
  /** Set/replace drug A (row axis). Null clears it. */
  onSelectA: (drug: CompatibilityDrug | null) => void
  /** Set/replace drug B (column axis). Null clears it. */
  onSelectB: (drug: CompatibilityDrug | null) => void
}

type SlotKey = 'a' | 'b'

export function PairPicker({
  state,
  colDrugs,
  drugA,
  drugB,
  onSelectA,
  onSelectB,
}: PairPickerProps) {
  const { t } = useTranslation()
  // Which slot's picker is open (null = closed). The picker is a full-screen
  // sheet so a one-handed nurse scans the whole list, not a cramped popover.
  const [openSlot, setOpenSlot] = useState<SlotKey | null>(null)

  // The picker sheet is full-screen, TOP-anchored, and sized to the visual
  // viewport so the iOS keyboard can never clip it (see SheetContent below).
  // Gated on `openSlot` so the resize listener is live ONLY while the sheet is
  // open — a closed picker costs zero re-renders on keyboard/orientation change.
  const vvHeight = useVisualViewportHeight(openSlot !== null)

  // Deferred autofocus for the picker's search input: focus only AFTER the
  // sheet's open animation settles so the iOS keyboard doesn't rise mid-slide
  // (the dark broken-flash bug). Called here at the SheetContent site and gated
  // on the open state so it re-arms on each open; `searchInputRef` threads down
  // to the input, and `preventPickerAutoFocus` suppresses Radix's t=0 focus.
  const { ref: searchInputRef, onOpenAutoFocus: preventPickerAutoFocus } =
    useDeferredAutoFocus<HTMLInputElement>(openSlot !== null)

  const cell = useMemo<CompatibilityCell | null>(() => {
    if (!drugA || !drugB) return null
    return getCell(state, drugA.id, drugB.id)
  }, [state, drugA, drugB])

  // A self-pair (same drug on both axes) can't be CREATED here — the picker
  // excludes the other slot's drug — but it CAN be inherited from the shared
  // board selection: a self-pair set on the wide-screen grid (row-header A +
  // col-header A) and then carried onto this surface when the viewport narrows
  // below `md` (e.g. a large iPhone rotated landscape→portrait). getCell returns
  // null for it, so without this branch it would fall through to the "not in
  // this matrix" note — misleading, since the pair is meaningless, not
  // unstudied.
  const selfPair = !!drugA && !!drugB && drugA.id === drugB.id

  // Grounded SAI/dm+d medication cards for the picked drugs, rendered inline
  // below the verdict. Same lookup the desktop DrugSidePanel uses — but here
  // it lives ON the one scrollable phone surface instead of a separate overlay
  // (an overlay that auto-opened on first pick would hijack the screen and,
  // on dismiss, clear the pair before the second drug could be chosen). Hooks
  // run unconditionally; the cards own their own loading/error/not-found state.
  const pickedDrugs = useMemo(
    () => [drugA, drugB].filter((d): d is CompatibilityDrug => d !== null),
    [drugA, drugB],
  )
  const lookups = useMedicationLookups(pickedDrugs.map(drugSearchTerm))

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]">
      {/* The two slots, stacked, with a centred "↔" between them. */}
      <div className="flex flex-col gap-2">
        <PairSlot
          label={t('compatibility.pairPicker.slotA', 'Drug A')}
          drug={drugA}
          onOpen={() => setOpenSlot('a')}
          onClear={() => onSelectA(null)}
        />
        <div className="flex items-center justify-center" aria-hidden>
          <span className="text-[15px] font-semibold text-neutral-400">↔</span>
        </div>
        <PairSlot
          label={t('compatibility.pairPicker.slotB', 'Drug B')}
          drug={drugB}
          onOpen={() => setOpenSlot('b')}
          onClear={() => onSelectB(null)}
        />
      </div>

      {/* Verdict region — the answer. Four shapes: pick-both prompt, self-pair
          note, no-data note, and a tone-coded verdict card. */}
      <div className="mt-1">
        {!drugA || !drugB ? (
          <PromptCard />
        ) : selfPair ? (
          <NoteCard text={t('compatibility.selfPairNote')} />
        ) : !cell ? (
          <NoteCard text={t('compatibility.noDataReasons.pair')} />
        ) : (
          <VerdictCard cell={cell} drugA={drugA} drugB={drugB} />
        )}
      </div>

      {/* Grounded drug information — one card per picked drug, inline on the
          same scroll. Only shown once at least one drug is chosen. */}
      {pickedDrugs.length > 0 && (
        <section className="mt-1">
          <p className="flex items-center gap-1.5 font-body text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-400">
            <Pill className="h-3 w-3" aria-hidden />
            {t('compatibility.panel.drugInfoTitle', 'Drug information')}
          </p>
          <div className="mt-2.5 flex flex-col gap-2.5">
            {lookups.map((lookup, i) => (
              <MedicationCard key={pickedDrugs[i].id} lookup={lookup} />
            ))}
          </div>
        </section>
      )}

      {/* Full-screen drug picker. One Sheet instance, re-keyed by slot, so the
          search field resets between A and B. Bottom sheet — natural one-handed
          reach on iOS Safari. */}
      <Sheet open={openSlot !== null} onOpenChange={(o) => !o && setOpenSlot(null)}>
        <SheetContent
          side="top"
          data-testid="pair-picker-drug-search"
          // TOP-anchored, full-height, sized to the VISUAL viewport — not a
          // bottom-anchored `dvh` sheet. On iOS Safari the keyboard leaves
          // `dvh` untouched but shrinks the visual viewport, so a `dvh` bottom
          // sheet runs its lower half behind the keyboard AND, when Safari
          // scrolls the focused search input into view, gets dragged upward —
          // clipping its header off the top so the list spills under the status
          // bar. `side="top"` anchors BOTH the panel and its slide-in animation
          // to the top edge. (A `side="bottom"` panel force-moved to the top
          // with `!top-0` still animates IN from the bottom — sliding up from
          // below the fold while the keyboard rises, which is the dark
          // broken-flash seen mid-open.) Sizing to `visualViewport.height`
          // (inline, `100dvh` fallback) keeps the whole list above the keyboard;
          // `pt`/`pb` clear the safe-area insets. Focus is deferred via
          // `useDeferredAutoFocus` (its `onOpenAutoFocus` suppresses Radix's t=0
          // focus here; the ref is threaded to the search input) so the keyboard
          // rises only AFTER the slide settles, never racing the animation.
          //
          // Width is owned by the Sheet's `inset-x-0` (left:0; right:0). We
          // deliberately do NOT set `w-full`/`max-w-[100vw]`: with
          // `viewport-fit=cover`, `100vw` is the full physical width (ignoring
          // the safe-area gutters), and on a fixed element a `width` overrides
          // the `right:0` inset — left-anchoring the panel so it spills past the
          // right edge and clips the right-aligned ATC code. `overflow-x-hidden`
          // stays as a clamp.
          onOpenAutoFocus={preventPickerAutoFocus}
          style={vvHeight != null ? { height: `${vvHeight}px` } : undefined}
          className="flex h-[100dvh] min-w-0 max-w-full flex-col gap-0 overflow-x-hidden !border-0 p-0 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]"
        >
          <SheetTitle className="sr-only">
            {openSlot === 'a'
              ? t('compatibility.pairPicker.slotA', 'Drug A')
              : t('compatibility.pairPicker.slotB', 'Drug B')}
          </SheetTitle>
          {openSlot && (
            <DrugSearchList
              key={openSlot}
              inputRef={searchInputRef}
              drugs={colDrugs}
              selectedId={openSlot === 'a' ? drugA?.id ?? null : drugB?.id ?? null}
              // Exclude the OTHER slot's drug so the picker can't PUT the same
              // drug on both axes (a Y-site self-mix is not meaningful). An
              // inherited self-pair from the shared board state is caught by the
              // selfPair note above, not created here.
              excludeId={openSlot === 'a' ? drugB?.id ?? null : drugA?.id ?? null}
              onPick={(drug) => {
                if (openSlot === 'a') onSelectA(drug)
                else onSelectB(drug)
                setOpenSlot(null)
              }}
              onClose={() => setOpenSlot(null)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}

/**
 * One drug slot — either an empty "add" affordance or the picked drug with a
 * clear (×) button. Tapping the body opens the picker. ≥56px tall: a generous
 * one-handed target well above the 44px floor.
 */
function PairSlot({
  label,
  drug,
  onOpen,
  onClear,
}: {
  label: string
  drug: CompatibilityDrug | null
  onOpen: () => void
  onClear: () => void
}) {
  const { t } = useTranslation()
  const split = drug ? splitLabel(drug.label) : null
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-[14px] border bg-[var(--canvas)] px-4',
        drug ? 'border-primary-500' : 'border-dashed border-[var(--hairline)]',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={
          drug
            ? t('compatibility.pairPicker.change', 'Change {{slot}}', { slot: label })
            : t('compatibility.pairPicker.pick', 'Pick {{slot}}', { slot: label })
        }
        className="flex min-h-14 min-w-0 flex-1 items-center gap-3 py-2.5 text-left transition active:scale-[0.99]"
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-400">
          {label}
        </span>
        {drug && split ? (
          <span className="min-w-0 flex-1">
            <span className="block truncate font-display text-[16px] font-semibold tracking-[-0.2px] text-neutral-900">
              {split.name || drug.label}
            </span>
            {split.concentration && (
              <span className="block truncate text-[12px] text-neutral-500">
                {split.concentration}
              </span>
            )}
          </span>
        ) : (
          <span className="flex flex-1 items-center gap-1.5 text-[15px] text-neutral-400">
            <Plus className="h-4 w-4" aria-hidden />
            {t('compatibility.pairPicker.pickShort', 'Choose a drug')}
          </span>
        )}
      </button>
      {drug && (
        <button
          type="button"
          onClick={onClear}
          aria-label={t('compatibility.pairPicker.clear', 'Clear {{slot}}', { slot: label })}
          className="-mr-1.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 transition active:scale-95 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      )}
    </div>
  )
}

/** Empty-verdict prompt — both slots needed before there's anything to say. */
function PromptCard() {
  const { t } = useTranslation()
  return (
    <div className="rounded-[14px] border border-[var(--divider)] bg-[var(--canvas)] px-4 py-6 text-center">
      <p className="text-[15px] text-neutral-500">
        {t('compatibility.pairPicker.prompt', 'Pick two drugs to see their Y-site verdict.')}
      </p>
    </div>
  )
}

/** Neutral factual note (self-pair, or pair absent from the matrix). */
function NoteCard({ text }: { text: string }) {
  return (
    <div className="rounded-[14px] border border-[var(--divider)] bg-neutral-50 px-4 py-4">
      <p className="text-[14px] leading-relaxed text-neutral-600">{text}</p>
    </div>
  )
}

/**
 * The verdict card — tone wash + tone dot + tone-coloured headline + the pair
 * line. Status carries colour AND a label AND a dot, never colour alone.
 */
function VerdictCard({
  cell,
  drugA,
  drugB,
}: {
  cell: CompatibilityCell
  drugA: CompatibilityDrug
  drugB: CompatibilityDrug
}) {
  const { t } = useTranslation()
  const tone: VerdictTone = VERDICT_TONE[cell.verdict]
  const nameA = shortLabel(drugA)
  const nameB = shortLabel(drugB)
  return (
    <div
      className={cn(
        'rounded-[14px] border border-[var(--divider)] px-4 py-4',
        TONE_TINT[tone],
      )}
    >
      <p className="font-body text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">
        {t('compatibility.panel.verdictTitle', 'Y-site verdict')}
      </p>
      <p className="mt-1 text-[13px] font-semibold text-neutral-700">
        {nameA} ↔ {nameB}
      </p>
      <div className="mt-2.5 flex items-start gap-2.5">
        <span
          aria-hidden
          className={cn(
            'mt-1 h-3 w-3 flex-shrink-0 rounded-full',
            TONE_FILL[tone],
          )}
        />
        <p
          className={cn(
            'font-display text-[18px] font-semibold leading-tight tracking-[-0.3px]',
            TONE_TEXT[tone],
          )}
        >
          {t(`compatibility.verdicts.${cell.verdict}`)}
        </p>
      </div>
      {cell.derived && (
        <p className="mt-3 rounded-lg bg-[color-mix(in_srgb,var(--canvas)_70%,transparent)] px-3 py-2.5 text-[12px] leading-relaxed text-neutral-700">
          {t('compatibility.derivedNote')}
        </p>
      )}
    </div>
  )
}

/**
 * Full-screen searchable drug list inside the picker sheet. Sticky search
 * header; the rest scrolls. Each row is a ≥48px tap target with name +
 * concentration. The currently-selected drug (if re-opening the same slot) is
 * marked.
 */
function DrugSearchList({
  inputRef,
  drugs,
  selectedId,
  excludeId,
  onPick,
  onClose,
}: {
  /** Deferred-autofocus ref for the search input, owned by PairPicker so the
   *  focus timer re-arms per open (this component remounts per slot via `key`,
   *  but the ref/timer must outlive that). */
  inputRef: RefObject<HTMLInputElement | null>
  drugs: CompatibilityDrug[]
  selectedId: string | null
  /** The drug picked in the OTHER slot — removed from the list so the two axes
   *  can never hold the same drug. */
  excludeId: string | null
  onPick: (drug: CompatibilityDrug) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const filtered = useMemo(() => {
    const pool = excludeId ? drugs.filter((d) => d.id !== excludeId) : drugs
    const term = q.trim().toLowerCase()
    if (!term) return pool
    return pool.filter((d) =>
      [d.label, d.atc, d.snomed].filter(Boolean).join(' ').toLowerCase().includes(term),
    )
  }, [drugs, excludeId, q])

  return (
    <>
      <div className="flex min-w-0 items-center gap-2 border-b border-[var(--divider)] py-2.5 pl-3 pr-[max(0.75rem,env(safe-area-inset-right))]">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.back', 'Back')}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-neutral-500 transition active:scale-95 hover:bg-neutral-100"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </button>
        <label className="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-full border border-[var(--hairline)] bg-[var(--canvas)] px-4 focus-within:border-primary-500">
          <Search className="h-4 w-4 flex-shrink-0 text-neutral-400" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('compatibility.search.placeholder', 'Search a drug …')}
            aria-label={t('compatibility.search.placeholder', 'Search a drug …')}
            className="min-w-0 flex-1 bg-transparent text-[16px] text-neutral-900 outline-none placeholder:text-neutral-400"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              aria-label={t('compatibility.search.clear', 'Clear search')}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 transition active:scale-95 hover:bg-neutral-100"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </label>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-1 items-center justify-center px-6 py-10 text-center text-[15px] text-neutral-500">
          {t('compatibility.pairPicker.noResults', 'No drug matches your search.')}
        </div>
      ) : (
        <ul className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain py-1">
          {filtered.map((d) => {
            const { name, concentration } = splitLabel(d.label)
            const active = d.id === selectedId
            return (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => onPick(d)}
                  className={cn(
                    'flex min-h-12 w-full items-center gap-3 py-2.5 pl-4 pr-[max(1rem,env(safe-area-inset-right))] text-left transition active:bg-neutral-100',
                    active && 'bg-primary-50',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate text-[16px] leading-snug',
                        active ? 'font-semibold text-primary-700' : 'text-neutral-900',
                      )}
                    >
                      {name || d.label}
                    </span>
                    {concentration && (
                      <span className="block truncate text-[12px] text-neutral-500">
                        {concentration}
                      </span>
                    )}
                  </span>
                  {d.atc && (
                    <span className="flex-shrink-0 font-mono text-[11px] text-neutral-400">
                      {d.atc}
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}
